import {
  createTRPCContext,
  createTRPCOptionsProxy,
} from "@trpc/tanstack-react-query";
import { QueryClient } from "@tanstack/react-query";
import {
  createTRPCClient,
  httpBatchLink,
  httpLink,
  splitLink,
} from "@trpc/client";
import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "../../api/src/trpcRouter";
import { getEnvVar } from "./common/utils/environment";
import { supabase } from "./ajax/clients/supabase";
import { getImpersonationHeaders } from "@/features/impersonation/impersonation-session";

export const queryClient = new QueryClient();

export const { TRPCProvider, useTRPC, useTRPCClient } =
  createTRPCContext<AppRouter>();

/**
 * Check if the input contains FormData (needed for file uploads)
 * FormData cannot be batched, so we need to use httpLink for these requests
 */
function isFormData(input: unknown): boolean {
  return input instanceof FormData;
}

const getHeaders = async () => {
  const { data } = await supabase.auth.getSession();
  if (!data.session?.access_token) {
    return {};
  }
  return {
    authorization: `Bearer ${data.session.access_token}`,
    ...getImpersonationHeaders(),
  };
};

export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    splitLink({
      condition: (op) => isFormData(op.input) || op.context.skipBatch === true,
      true: httpLink({
        url: `${getEnvVar("VITE_PUBLIC_API_HOST")}/trpc`,
        headers: getHeaders,
      }),
      false: httpBatchLink({
        url: `${getEnvVar("VITE_PUBLIC_API_HOST")}/trpc`,
        headers: getHeaders,
      }),
    }),
  ],
});

export const trpc = createTRPCOptionsProxy<AppRouter>({
  queryClient: queryClient,
  client: trpcClient,
});

export type { AppRouter };
export type RouterOutputs = inferRouterOutputs<AppRouter>;
export type RouterInputs = inferRouterInputs<AppRouter>;
